import { Plus, X } from "lucide-react";
import type { Guest } from "../services/schedulerApi";
import { GUEST_EMAIL, MAX_GUESTS } from "../utils/guests";

// Extra people the client invites to the meeting: they get the calendar invite too.
const GuestFields: React.FC<{ guests: Guest[]; onChange: (guests: Guest[]) => void; disabled?: boolean;
  invalid?: boolean }> = ({ guests, onChange, disabled, invalid }) => {
  const update = (index: number, key: keyof Guest, value: string) =>
    onChange(guests.map((g, i) => (i === index ? { ...g, [key]: value } : g)));
  const input = "w-full rounded-lg border border-borderSubtle bg-white px-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50";

  return (
    <fieldset className="mt-8">
      <legend className="block text-sm font-medium text-text-primary mb-1">Invite colleagues (optional)</legend>
      <p className="text-sm text-text-secondary mb-4">
        They&apos;ll get the calendar invite too. Up to {MAX_GUESTS} guests.
      </p>
      {guests.map((guest, index) => (
        <div key={index} className="flex flex-col sm:flex-row gap-3 mb-3">
          <input
            type="text"
            inputMode="email"
            autoComplete="off"
            aria-label={`Guest ${index + 1} email`}
            placeholder="name@company.com"
            value={guest.email}
            disabled={disabled}
            aria-invalid={invalid && Boolean(guest.email || guest.name) && !GUEST_EMAIL.test(guest.email.trim())}
            onChange={(event) => update(index, "email", event.target.value)}
            className={input}
          />
          <input
            type="text"
            autoComplete="off"
            aria-label={`Guest ${index + 1} name`}
            placeholder="Name (optional)"
            value={guest.name}
            disabled={disabled}
            onChange={(event) => update(index, "name", event.target.value)}
            className={input}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange(guests.filter((_, i) => i !== index))}
            aria-label={`Remove guest ${index + 1}`}
            className="inline-flex shrink-0 items-center justify-center rounded-full border border-borderSubtle bg-white w-11 h-11 self-end sm:self-auto text-text-primary hover:bg-surfaceSoft"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      {invalid && <p className="text-red-500 text-xs mb-3">Please enter a valid email for each guest.</p>}
      {guests.length < MAX_GUESTS && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange([...guests, { email: "", name: "" }])}
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
        >
          <Plus className="w-4 h-4" /> Add a guest
        </button>
      )}
    </fieldset>
  );
};

export default GuestFields;
